import { useState, useCallback, useRef, useEffect } from 'react';
import { payAmigoClient, QuoteResponse, QuoteRequest } from '../api/payamigo';

interface UsePayAmigoQuoteResult {
  quote: QuoteResponse | null;
  isLoading: boolean;
  error: string | null;
  fetchQuote: (params: QuoteRequest) => Promise<void>;
  clearQuote: () => void;
  isQuoteExpired: boolean;
}

export const usePayAmigoQuote = (): UsePayAmigoQuoteResult => {
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isQuoteExpired, setIsQuoteExpired] = useState<boolean>(false);
  
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const expirationTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearQuote = useCallback(() => {
    setQuote(null);
    setError(null);
    setIsQuoteExpired(false);
    
    if (expirationTimerRef.current) {
      clearTimeout(expirationTimerRef.current);
      expirationTimerRef.current = null;
    }
  }, []);

  const fetchQuote = useCallback(async (params: QuoteRequest) => {
    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Clear previous expiration timer
    if (expirationTimerRef.current) {
      clearTimeout(expirationTimerRef.current);
      expirationTimerRef.current = null;
    }

    // Debounce the API call
    debounceTimerRef.current = setTimeout(async () => {
      setIsLoading(true);
      setError(null);
      setIsQuoteExpired(false);
      // Keep previous quote visible during refresh

      try {
        const response = await payAmigoClient.getQuote(params);
        setQuote(response);

        // Set up expiration timer
        // Parse expires_at as UTC and calculate time difference
        const expiresAt = new Date(response.expires_at);
        const now = new Date();
        
        // Calculate time difference in milliseconds
        let timeUntilExpiration = expiresAt.getTime() - now.getTime();
        
        // Refresh 5 seconds before expiration to avoid edge cases
        const REFRESH_BUFFER_MS = 5000; // 5 seconds
        timeUntilExpiration = Math.max(0, timeUntilExpiration - REFRESH_BUFFER_MS);

        if (timeUntilExpiration > 0) {
          expirationTimerRef.current = setTimeout(() => {
            setIsQuoteExpired(true);
          }, timeUntilExpiration);
        } else {
          setIsQuoteExpired(true);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch quote';
        setError(errorMessage);
        console.error('Quote fetch error:', err);
      } finally {
        setIsLoading(false);
      }
    }, 500); // 500ms debounce
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (expirationTimerRef.current) {
        clearTimeout(expirationTimerRef.current);
      }
    };
  }, []);

  return {
    quote,
    isLoading,
    error,
    fetchQuote,
    clearQuote,
    isQuoteExpired,
  };
};

